import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  Signal,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import Chart, { ChartConfiguration, ChartConfigurationCustomTypesPerDataset } from 'chart.js/auto';
import 'chartjs-adapter-dayjs-4';
import { getMinMaxDate } from '../../services/assessmentsHistory.functions';
import { AssessmentChartDataSets, AssessmentType } from '../../services/model';

@Component({
  selector: 'app-line-chart',
  standalone: true,
  imports: [],
  template: '<canvas style="width:100%" #canvas [id]="chartId">{{ chart }}</canvas>',
})
export class LineChartComponent implements OnInit {
  @Input() data!: Signal<AssessmentChartDataSets | undefined>;
  @Input() chartId!: string;
  @Input() type!: 'bar' | 'line';
  @ViewChild('canvas', { read: ElementRef, static: true }) canvas!: ElementRef<any>;
  public chart: any;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    console.log('constructor LineChartComponent');

    effect(() => {
      this.data();
      if (this.chart) {
        console.log('effect LineChartComponent');
        this.createChart();
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      console.log('ngOnInit LineChartComponent');
      this.createChart();
      this.cdr.detectChanges();
    }, 50);
  }

  createChart() {
    console.log('createChart');
    const datasetsObject: AssessmentChartDataSets = this.data() ?? {};
    const keys: Array<AssessmentType> = Object.keys(datasetsObject) as Array<AssessmentType>;
    const datasets = keys.map((type: AssessmentType) => {
      return {
        label: type,
        data: datasetsObject[type]?.map(row => ({ x: new Date(new Date(row.dateIso).toDateString()), y: row.score })),
        borderWidth: 1,
        // tension: 0.3,
      };
    });

    // const labelDataFull: Array<Date> = keys.flatMap((type: AssessmentType) => {
    //   return datasetsObject[type]!.map(row => new Date(new Date(row.dateIso).toDateString()));
    // });
    // labelDataFull.sort((a, b) => a.getTime() - b.getTime());
    // const maxDate = dayjs(labelDataFull[labelDataFull.length - 1])
    //   .add(3, 'day')
    //   .toISOString();
    // const minDate = dayjs(labelDataFull[0]).subtract(3, 'day').toISOString();

    const { minDate, maxDate, labelDataFull } = getMinMaxDate(datasetsObject);

    const config = this.getChartConfig(datasets, minDate, maxDate, labelDataFull);

    if (this.chart) {
      this.chart.data = { labels: labelDataFull, datasets: datasets };
      this.chart.options.scales.x.min = minDate;
      this.chart.options.scales.x.max = maxDate;
      this.chart.update();
    } else {
      this.chart = new Chart(this.canvas.nativeElement, config);
    }
  }

  getChartConfig(
    datasets: any,
    minDate: any,
    maxDate: any,
    labelDataFull: any
  ): ChartConfiguration | ChartConfigurationCustomTypesPerDataset {
    return {
      type: 'line',
      data: {
        labels: labelDataFull,
        datasets: datasets,
      },
      options: {
        responsive: true,
        aspectRatio: 2.5,
        animation: {
          delay: 200,
        },
        layout: {
          autoPadding: true,
          //   padding: {
          //     left: -10,
          //     right: 10,
          //   },
        },
        scales: {
          y: {
            reverse: true,
            position: 'right',
            afterBuildTicks: scale => {
              scale.ticks = [
                {
                  value: 0,
                },
                {
                  value: 1,
                },
                {
                  value: 2,
                },
                {
                  value: 3,
                },
                {
                  value: 4,
                },
              ];
            },
            min: -0.5,
            max: 4.5,
            ticks: {
              stepSize: 1,
              callback: (tickValue: string | number, index: number, ticks: Array<any>) => {
                if (tickValue === 5 || tickValue === -1) {
                  return;
                } else {
                  return tickValue;
                }
              },
            },
            beginAtZero: true,
            grid: {
              display: false,
            },
          },
          x: {
            // beginAtZero: true,
            grid: {
              display: true,
            },
            type: 'time',
            // time: {
            //   tooltipFormat: 'DD.MM.YY',
            //   unit: 'day',
            // },
            min: minDate,
            max: maxDate,
            time: {
              unit: 'week',
              isoWeekday: true,
              displayFormats: {
                week: 'dd, DD.MM.YY',
              },
            },
          },
        },
        plugins: {
          legend: {
            position: 'top',
          },
        },
      },
      plugins: [this.customCanvasBackgroundColor], // this.customLegend
    };
  }

  drawChartBackground(gradientBack: any) {
    gradientBack.addColorStop(0, 'rgba(255,255,255,1)');
    gradientBack.addColorStop(0.2, 'rgba(255,255,255,1)');

    gradientBack.addColorStop(0.2, 'rgba(252,252,239,1)');
    gradientBack.addColorStop(0.4, 'rgba(252,252,239,1)');

    gradientBack.addColorStop(0.4, 'rgba(252,244,219,1)');
    gradientBack.addColorStop(0.6, 'rgba(252,244,219,1)');

    gradientBack.addColorStop(0.6, 'rgba(255,228,200,1)');
    gradientBack.addColorStop(0.8, 'rgba(255,228,200,1)');

    gradientBack.addColorStop(0.8, 'rgba(251,221,221,1)');
    gradientBack.addColorStop(1, 'rgba(251,221,221,1)');
  }

  drawChartBackgroundReverse(gradientBack: any) {
    gradientBack.addColorStop(0, 'rgba(251,221,221,1)');
    gradientBack.addColorStop(0.2, 'rgba(251,221,221,1)');

    gradientBack.addColorStop(0.2, 'rgba(255,228,200,1)');
    gradientBack.addColorStop(0.4, 'rgba(255,228,200,1)');

    gradientBack.addColorStop(0.4, 'rgba(252,244,219,1)');
    gradientBack.addColorStop(0.6, 'rgba(252,244,219,1)');

    gradientBack.addColorStop(0.6, 'rgba(252,252,239,1)');
    gradientBack.addColorStop(0.8, 'rgba(252,252,239,1)');

    gradientBack.addColorStop(0.8, 'rgba(255,255,255,1)');
    gradientBack.addColorStop(1, 'rgba(255,255,255,1)');
  }

  customCanvasBackgroundColor = {
    id: 'customCanvasBackgroundColor',
    beforeDraw: (chart: any, args: any, options: any) => {
      const {
        ctx,
        chartArea: { left, right, top, bottom, width, height },
      } = chart;
      const canvas = chart.canvas;
      const chartArea = chart.chartArea;

      ctx.save();
      // Chart background
      const gradientBack = canvas.getContext('2d').createLinearGradient(right, bottom, right, top);
      // this.drawChartBackground(gradientBack);
      this.drawChartBackgroundReverse(gradientBack);
      ctx.fillStyle = gradientBack;
      ctx.fillRect(
        chartArea.left,
        chartArea.bottom,
        chartArea.right - chartArea.left,
        chartArea.top - chartArea.bottom
      );
    },
  };

  // https://www.youtube.com/watch?v=WHlAwsSnEoc
  customLegend = {
    id: 'customLegend',
    beforeDraw: (chart: any, args: any, options: any) => {
      const {
        ctx,
        data,
        chartArea: { left, right, top, bottom, width, height },
      } = chart;
      ctx.save();

      data.datasets.forEach((dataset: any, index: any) => {
        ctx.font = 'bolder 12px sans-serif';
        ctx.fillStyle = dataset.borderColor;
        ctx.textAlign = 'right';
        ctx.fillText(dataset.label, left - 25, chart.getDatasetMeta(index).data[0].y);
      });
    },
  };
}
